import {
  EnclosureIP,
  EnclosureType,
  IEnclosureRules,
  IRuleException,
  KitGroupOrder,
  KitType
} from '../models/prisma-xs.model';
import { RESIZE_DIRECTIONS } from '../../widget-grid/Utils';
import { IArticle } from '../../prisma-xs/models/article.model';
import { IDevice } from '../models/device.model';
import { SearchArticlePipe } from '../../ui/pipes/search-article.pipe';
import { ICatalogueDrag } from '../../prisma-xs/models/prisma-xs.model';
import { SearchGroupedCataloguePipe } from '../../enclosure/pipes/search-grouped-catalogue.pipe';
import { PrismaProductsPipe } from '../../enclosure/pipes/prisma-products.pipe';
import { ActiveProductsPipe } from '../../enclosure/pipes/active-products.pipe';

const extractEnclosureRule = (enclosureType: EnclosureType, ipType: EnclosureIP, rules: IEnclosureRules[]) => {
  const ruleForEnclosureType = rules.find(item => item.enclosureType === enclosureType);
  const ruleForEnclosureTypeIP = ruleForEnclosureType.rules.find(item => item.ipType === ipType);
  return ruleForEnclosureTypeIP.rule;
};

export const generateArrayFromMinAndMax = (min: number, max: number) => {
  const array = [];
  for (let i = min; i <= max; i++) {
    array.push(i);
  }
  return array;
};

export const getRowsFields = (enclosureType: EnclosureType, ipType: EnclosureIP, rules: IEnclosureRules[]) => {
  const rule = extractEnclosureRule(enclosureType, ipType, rules);
  return {
    fields: generateArrayFromMinAndMax(rule.fields.min, rule.fields.max),
    rows: generateArrayFromMinAndMax(rule.rows.min, rule.rows.max)
  };
};

// eslint-disable-next-line max-len
export function getExceptionConstraintsRows(enclosureType: EnclosureType, ipType: EnclosureIP, rules: IEnclosureRules[], row?: number): number[] {
  const rule = extractEnclosureRule(enclosureType, ipType, rules);
  let min = rule.fields.min;
  let max = rule.fields.max;
  if (rule.enclosureRuleExceptions.length) {
    rule.enclosureRuleExceptions
      .filter((exception: IRuleException) => Number(row) >= exception.minRows && Number(row) <= exception.maxRows)
      .forEach((exception: IRuleException) => {
        min = exception.minFields;
        max = exception.maxFields;
      });
  }
  return generateArrayFromMinAndMax(min, max);
}

/**
 * @DEPRECATED
 */
export const isPrismaXsEnclosure = (enclosure) => {
  return enclosure && 'configuration' in enclosure;
};

export const getEnclosureResizeDirectionsByType = (enclosureType: EnclosureType): RESIZE_DIRECTIONS[] => {
  switch (enclosureType) {
    case EnclosureType.FloorStanding:
      return [RESIZE_DIRECTIONS.right];
    default:
      return [
        RESIZE_DIRECTIONS.bottom,
        RESIZE_DIRECTIONS.right,
        RESIZE_DIRECTIONS.bottomRight];
  }
};

export const getEnclosureResizeDirections = (placedKits: IArticle[], enclosureType: EnclosureType): RESIZE_DIRECTIONS[] => {
  if (enclosureType === EnclosureType.FloorStanding) {
    return [RESIZE_DIRECTIONS.right];
  }
  const allowOnlyWidthResizeKits = placedKits.filter(kit => kit.kitType === KitType.CompleteFields ||
    kit.kitOrder === KitGroupOrder.KITS_FOR_COMPLETE_MULTI_MEDIA_FIELDS
  );
  return allowOnlyWidthResizeKits.length ?
    [RESIZE_DIRECTIONS.right] : [RESIZE_DIRECTIONS.bottom, RESIZE_DIRECTIONS.right, RESIZE_DIRECTIONS.bottomRight];
};

export const calculateNumberOfKitsOnPrismaXsEnclosure = (devices: IDevice[]): number => {
  return devices?.filter(device => device.onConfiguration)?.length;
}

export const getSearchResultPosition = (articles: IArticle[], drag: ICatalogueDrag, catalogueArticleSelectedType: KitType): number => {
  const searchArticles = new SearchArticlePipe().transform(articles, drag.searchText)
    ?.filter(kit => kit.kitType === catalogueArticleSelectedType)
    ?.filter(kit => kit.kitTypeId === drag.article.kitTypeId);

  if (!searchArticles?.length) {
    return 0;
  }

  const index = searchArticles.findIndex(article => article.ref === drag.article.ref);

  if (index < 0) {
    return 0;
  }

  return index + 1
}

export const getNumberOfPragmaSearchedProducts = (articles: IArticle[], searchText: string): number => {
  const pragmaProductsPipe = new PrismaProductsPipe();

  const completeFieldsSearchResults = new SearchGroupedCataloguePipe()
    .transform(pragmaProductsPipe.transform(new ActiveProductsPipe().transform(articles), KitType.CompleteFields),
      searchText) ?? 0;

  const groupedArticlesSearchResults = new SearchGroupedCataloguePipe()
    .transform(pragmaProductsPipe.transform(new ActiveProductsPipe().transform(articles), KitType.CompleteEnclosures),
      searchText) ?? 0;

  return completeFieldsSearchResults + groupedArticlesSearchResults;
}

export const getNumberOfSearchedProducts = (articles: IArticle[], searchText: string): number => {
  const completeFieldsSearchResults = new SearchGroupedCataloguePipe()
    .transform(new PrismaProductsPipe()
        .transform(new ActiveProductsPipe().transform(articles), KitType.CompleteFields),
      searchText) ?? 0;

  const groupedArticlesSearchResults = new SearchGroupedCataloguePipe()
    .transform(new PrismaProductsPipe()
        .transform(new ActiveProductsPipe().transform(articles), KitType.ModularKits),
      searchText) ?? 0;

  return completeFieldsSearchResults + groupedArticlesSearchResults;
}
